import React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'

import { ChevronLeft, ChevronRight } from '@mxenabled/mx-icons'

import { useCopyStore } from '../context/hooks'

export interface CarouselControlsProps {
  currentBeatIndex: number
  numBeats: number
  onNextClick: () => void
  onPreviousClick: () => void
  scrollRightBoundary: number
  scrollX: number
  shouldApplyDotAnimation?: boolean
  visibleCardsCount?: number
}

export const CarouselControls: React.FC<CarouselControlsProps> = ({
  currentBeatIndex,
  numBeats,
  onNextClick,
  onPreviousClick,
  scrollRightBoundary,
  scrollX,
  shouldApplyDotAnimation = false,
  visibleCardsCount = 1,
}) => {
  const theme = useTheme()
  // *************** Context functions *********************
  const { carouselCopy: copy } = useCopyStore()

  const disabledLeft =
    Math.floor(scrollX) > scrollRightBoundary || currentBeatIndex - visibleCardsCount + 1 === 0
  const disabledRight = scrollX < 0 || numBeats - 1 === currentBeatIndex

  const DISABLED_ICON_COLOR = theme.palette.grey[500]
  const ICON_COLOR = theme.palette.primary.main

  return (
    <Stack
      direction={'row'}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        pt: 4,
        width: '100%',
        // we were using `gap` instead of margin, but it doesn't work in safari browsers older than iOS 14.1
        '& button:first-of-type': {
          mr: 8,
        },
      }}
    >
      <IconButton
        aria-disabled={disabledLeft}
        aria-label={copy?.previous_insight_cta_aria}
        disabled={disabledLeft}
        onClick={onPreviousClick}
      >
        <ChevronLeft
          sx={{
            color: disabledLeft ? DISABLED_ICON_COLOR : ICON_COLOR,
          }}
        />
      </IconButton>
      {Array.from({ length: numBeats }).map((_, i) => {
        const isActive = i === currentBeatIndex
        return (
          <Box
            className="mx-insights-micro-carousel-dots"
            key={`carousel-button-${i}`}
            sx={{
              bgcolor: isActive ? ICON_COLOR : DISABLED_ICON_COLOR,
              height: 8,
              mr: 8, // existing comment from meganthan "we were using `gap` instead of margin, but it doesn't work in safari browsers older than iOS 14.1"
              ...(shouldApplyDotAnimation
                ? {
                    borderRadius: '4px',
                    transformOrigin: 'center',
                    transition: 'all 0.5s ease-out',
                    willChange: 'width, background-color',
                  }
                : {
                    borderRadius: '50%',
                    p: 0,
                  }),
              width: isActive && shouldApplyDotAnimation ? 24 : 8,
            }}
          />
        )
      })}
      <IconButton
        aria-disabled={disabledRight}
        aria-label={copy?.next_insight_cta_aria}
        disabled={disabledRight}
        onClick={onNextClick}
      >
        <ChevronRight
          sx={{
            color: disabledRight ? DISABLED_ICON_COLOR : ICON_COLOR,
          }}
        />
      </IconButton>
    </Stack>
  )
}
